import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches render/runtime errors that would otherwise unmount the whole
// React tree and leave a blank page. Shows a visible message + the error
// instead, and logs the full stack to the console for debugging.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Barrelcast] Uncaught render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
          <AlertTriangle className="h-10 w-10 text-destructive" />
          <div>
            <h1 className="font-display text-xl text-foreground">Something broke</h1>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {this.state.error.message || "An unexpected error occurred."}
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 bg-gradient-sunset px-5 py-2.5 text-sm font-bold uppercase tracking-widest text-accent-foreground"
          >
            <RefreshCw className="h-4 w-4" /> Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
